import { useEffect, useRef, useState } from 'react'

import { print } from 'graphql'

import type {
  TransportPositionStreamEvent,
  WaypointGroup,
} from '../codegen/graphql'
import { PositionStreamSubscriptionDocument } from '../queries/robotTraining'
import {
  disposePositionStreamClient,
  getPositionStreamClient,
} from '../subscriptionClient'

/**
 * Position type used by UI components (CoordsCard, etc.)
 * This is a flattened representation of the WaypointGroup for easier UI consumption.
 * Note: x, y, z can be null when cartesian data is not available from the robot.
 */
export type RobotPosition = {
  x: number | null
  y: number | null
  z: number | null
  rail?: number | null
  rotation?: number | null
  joints?: number[] | null
}

type PositionStreamSubscriptionData = {
  transportPositionStream: TransportPositionStreamEvent
}

type UsePositionStreamSubscriptionOptions = {
  robotId: string
  fps?: number
  enabled?: boolean
  onPosition?: (position: RobotPosition) => void
  onError?: (error: string) => void
  onDisconnected?: () => void
}

type UsePositionStreamSubscriptionResult = {
  isConnected: boolean
  error: Error | null
  position: RobotPosition | null
}

/**
 * Transforms WaypointGroup from subscription to RobotPosition format
 * for use by UI components.
 */
function transformToRobotPosition(waypointGroup: WaypointGroup): RobotPosition {
  const cartesian = waypointGroup.cartesian
  const joint = waypointGroup.joint

  return {
    x: cartesian?.x ?? null,
    y: cartesian?.y ?? null,
    z: cartesian?.z ?? null,
    rail: cartesian?.rail ?? null,
    rotation: cartesian?.rotation ?? null,
    joints: joint?.joints ?? null,
  }
}

/**
 * Hook to subscribe to live robot position updates.
 * Receives position data streamed from the robot during training.
 */
export function disposePositionClient(): void {
  disposePositionStreamClient()
}

export function usePositionStreamSubscription(
  options: UsePositionStreamSubscriptionOptions,
): UsePositionStreamSubscriptionResult {
  const {
    robotId,
    enabled = true,
    onPosition,
    onError,
    onDisconnected,
  } = options
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [position, setPosition] = useState<RobotPosition | null>(null)

  const onPositionRef = useRef(onPosition)
  const onErrorRef = useRef(onError)
  const onDisconnectedRef = useRef(onDisconnected)

  useEffect(() => {
    onPositionRef.current = onPosition
  }, [onPosition])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  useEffect(() => {
    onDisconnectedRef.current = onDisconnected
  }, [onDisconnected])

  const { fps = 10 } = options

  useEffect(() => {
    if (!enabled || !robotId) {
      return
    }

    const client = getPositionStreamClient()

    const unsubscribe = client.subscribe<PositionStreamSubscriptionData>(
      {
        query: print(PositionStreamSubscriptionDocument),
        variables: { input: { robotId, fps } },
      },
      {
        next: (result) => {
          setIsConnected(true)
          const event = result.data?.transportPositionStream
          if (event?.position) {
            const transformedPosition = transformToRobotPosition(event.position)
            setPosition(transformedPosition)
            onPositionRef.current?.(transformedPosition)
          }
        },
        error: (err) => {
          // Log full error object for debugging
          console.error(
            'Position subscription error details:',
            JSON.stringify(err, null, 2),
          )
          const errorMessage =
            err instanceof Error ? err.message : JSON.stringify(err)
          setError(err instanceof Error ? err : new Error(errorMessage))
          onErrorRef.current?.(errorMessage)
          setIsConnected(false)
        },
        complete: () => {
          onDisconnectedRef.current?.()
          setIsConnected(false)
        },
      },
    )

    return () => {
      unsubscribe()
      setIsConnected(false)
    }
  }, [enabled, robotId, fps])

  return { isConnected, error, position }
}
